import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { RETOUCH_IMAGE } from "@/data/portfolioData";

const BeforeAfter = () => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8 lg:px-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-10"
      >
        <p className="eyebrow-label mb-4">The Retouching Difference</p>
        <h2 className="font-display max-w-2xl text-2xl font-bold tracking-tight text-zinc-50 sm:text-3xl lg:text-4xl">
          Drag to see raw shot vs. delivered image
        </h2>
        <p className="mt-4 max-w-xl text-base text-zinc-400">
          Every photo is hand-edited — exposure blending, window pulls, sky work, and colour grading — before it reaches your listing.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.1 }}
        ref={ref}
        data-testid="retouch-slider-container"
        className="relative aspect-[16/9] cursor-ew-resize select-none overflow-hidden rounded-2xl border border-white/10"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          update(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 1) update(e.clientX);
        }}
      >
        <img
          src={RETOUCH_IMAGE}
          alt="Edited twilight real estate photo"
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <img
            src={RETOUCH_IMAGE}
            alt="Raw unedited real estate photo"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ filter: "brightness(0.62) saturate(0.45) contrast(0.85)" }}
            draggable={false}
          />
        </div>

        <span
          data-testid="retouch-badge-raw"
          className="font-tech absolute left-5 top-5 rounded-full bg-[#0A0A0C]/70 px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] text-zinc-300 backdrop-blur-sm"
        >
          Raw
        </span>
        <span
          data-testid="retouch-badge-edited"
          className="font-tech absolute right-5 top-5 rounded-full bg-[#D4AF37]/90 px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] text-zinc-950"
        >
          Edited
        </span>

        <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
          <div className="absolute inset-y-0 -ml-px w-0.5 bg-[#D4AF37]" />
          <div
            data-testid="retouch-slider-drag-handle"
            className="absolute top-1/2 -ml-6 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-2 border-[#D4AF37] bg-[#0A0A0C]/80 text-[#E2C275] shadow-[0_0_24px_rgba(212,175,55,0.4)] backdrop-blur-sm"
          >
            <MoveHorizontal size={18} />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default BeforeAfter;
